// @intl project:microsite

import React, { MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { List } from 'immutable'
import { isEmpty } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { LOCAL_STORAGE_KEY as SHARED_APP_LOGIC_LOCAL_STORAGE_KEY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_ENTRY_POINTS,
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  INTEGRATED_BENEFITS_EVENTS,
  LOGIN_EVENTS,
  MIXPANEL_EVENT_ATTRIBUTES,
  PAGES,
  PATHWAYS_EVENTS,
  REFERRAL_ENTRY_POINTS,
  SMART_SEARCH_EVENTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  APP_ROUTE,
  FROM_URL_REDIRECT_KEY,
  HEALTH_CHALLENGES_SECURE_BASE_ROUTE,
  NEURODIVERSITY_SECURE_BASE_ROUTE,
  ONBOARDING_WELLNESS_GOALS_PATH,
  PAGE_ROUTES,
  WOMENS_HEALTH_SECURE_BASE_ROUTE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { UTM_SOURCE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'
import { useIsMobile } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsMobile'
import { clearStorageDataOnLogout } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/logoutUtils'
import {
  getShouldRouteDbtTeenLogoToCare,
  isDBTUser,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { SmartSearchExperimentJoinKeys } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/searchForCareExperimentTypes'
import { ExperimentUUIDs } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/types'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { GLOBAL_HEADER_ENABLED_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'
import { CARE_NAVIGATOR_TRIGGER_SOURCE, NavBarProps } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { AppContext } from '@lyrahealth-inc/ui-core-crossplatform/src/context'
import { useStorageWithExpiration } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useStorageWithExpiration'
import { Header as AppHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/header/Header'
import { HeaderNavMenuProps } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/headerNavMenu/HeaderNavMenu'
import { benefitsHeaderText } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/integratedBenefitsPage/IntegratedBenefitsPage'
import { getAuroraAwareBackgroundColor } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/commonStyles'
import { toJS } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hocs/toJS'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { ThemeScope } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeScope'

import styles from './header.module.scss'
import MobileHeader from './MobileHeader'
import NavBarHeader from './NavBarHeader'
import OnboardingNavBar from './OnboardingNavBar'
import { setToastContent } from '../../../data/appGlobals/appGlobalsActions'
import {
  getBeforeLaunch,
  getCustomerName,
  getCustomerWorkLifeBenefits,
  getHideExtraHeaderOptions,
  getIsClientHPI,
  getLibraryDisabled,
} from '../../../data/customer/customerSelectors'
import { resetHealthPlan, resetTriage, setIsLogoutInProgress } from '../../../data/logout/logoutActions'
import * as MixpanelActions from '../../../data/mixpanel'
import { MIXPANEL_EVENTS, trackEventWithObj } from '../../../data/mixpanel'
import mixpanel from '../../../data/mixpanel/mixpanel-browser'
import { convertRouteToMixpanelPage } from '../../../data/mixpanel/utils'
import {
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserInternational,
  getShouldShowAccountBadge,
} from '../../../data/user/userSelectors'
import { isPathInNeurodiversityFlow } from '../../../features/centerOfExcellence/centerOfExcellenceUtils'
import { isInLoginFlow } from '../../../features/login/loginUtils'
import MessagesPopover from '../../../features/lyraTherapy/messagesPopover/MessagesPopover'
import { clearDirectLinkSource, clearWellnessGoals } from '../../../features/onboard/data/onboardDataActions'
import { getOnboardDirectLinkSource } from '../../../features/onboard/data/onboardSelectors'
import {
  HPI_ROUTES,
  PROFILE_CONFIRMATION_PAGES,
  PROFILE_EDIT_PAGES,
} from '../../../features/onboard/data/page-navigation/location-actions'
import { useFavoritedProvidersEntrypointNav } from '../../../features/onboard/favoritedProviders/hooks/useFavoritedProvidersEntrypointNav'
import { isInRegisterFlow, isPathInAutoEnrollmentFlow } from '../../../features/register/data/registerUtils'
import { clearTriage } from '../../../features/triage/data/triageActions'
import {
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
} from '../../../features/wellnessCheckIn/data/wellnessCheckInActions'
import { useDbtTeenCareStatus } from '../../../hooks/useDbtTeenCareStatus'
import { LOCAL_STORAGE_KEY } from '../../constants/appConstants'
import {
  AUTO_ENROLLMENT_PATHWAYS_PATH,
  PARENT_LED_TEEN_WELCOME_PAGE_PATH,
  REQUIRE_ELIGIBILITY_FORM_PATH,
} from '../../constants/registrationFormConstants'
import { withTrackIntegratedBenefits } from '../../hocs/withTrackIntegratedBenefits'
import { useCareNavigatorModal } from '../../hooks/useCareNavigatorModal'
import { useDefaultMixpanelPage } from '../../hooks/useDefaultMixpanelPage'
import { useReferral } from '../../hooks/useReferral'
import { broadcastSessionTermination } from '../../hooks/useSessionSync'
import { rerouteFromMicrosite } from '../../utils/micrositeUtils'
import { clearPendoSession } from '../../utils/pendoLoader'
import { updateStoredProviderResultsSurveyValues } from '../../utils/pendoUtils'
import { getIsProviderResultsPage } from '../../utils/routingUtils'
import { matchPaths } from '../../utils/stringUtils'
import { hasRole, toggleIsSessionTimerDisabled } from '../../utils/userUtils'

/**
 * Renders nothing, but reports whether the logged-in app header is mounted via its own
 * mount/unmount lifecycle. Rendered alongside `appHeader` so it stays accurate across the
 * header's early returns (which would otherwise leave a render-time flag stale).
 */
const AppHeaderVisibilityReporter = ({ setIsShowing }: { setIsShowing: (isShowing: boolean) => void }) => {
  useEffect(() => {
    setIsShowing(true)
    return () => setIsShowing(false)
  }, [setIsShowing])
  return null
}

export const Header = ({
  isBlurBackground,
  user,
  videoSessionOpen,
  isLyraTherapy,
  appointmentSize,
  libraryDisabled,
  beforeLaunch,
  isUserInternational,
  resetHealthPlan,
  resetTriage,
  isClientHPI,
  trackEventWithObj,
  customerName,
  isUserAssumedInternational,
  trackIntegratedBenefits,
  setToastContent,
  shouldShowAccountBadge,
  currentDirectPathLinkSource,
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
  setIsLogoutInProgress,
  clearWellnessGoals,
  clearDirectLinkSource,
  clearTriage,
  logoFillColor,
}: $TSFixMe) => {
  const intl = useIntl()
  const {
    breakpoints: { isMobileSized, isMinWidthLaptop, isMinWidthTablet },
    colors,
    isAuroraTheme,
  } = useTheme() as ThemeType
  const navigate = useNavigate()
  const { pathname, search, state } = useLocation()
  const {
    isLanguageSelectorEnabled,
    isDisplayLanguageEnabled,
    shouldShowCareNavigatorHeaderEntrypoint,
    isReferralEnabled,
    shouldShowCareTabInitial,
    adultTriageDisabled,
    psaBanner,
    isUngatedEssentialsEventsEnabled,
    isRegistrationAndLoginDisabled: micrositeAndElsewhereRegistrationAndLoginDisabled,
    globalHeaderConfig,
    isDBTTeensEnabled,
  } = useFlags()
  const { isLyraSelect } = useContext(ProductTypeContext)
  const { sendExperimentData } = useContext(AppContext)
  const [isShowingAppHeader, setIsShowingAppHeader] = useState(false)

  const showCareNavigatorInHamburgerMenu = isMobileSized && isAuroraTheme
  const triggerSourceRef = useRef<CARE_NAVIGATOR_TRIGGER_SOURCE>(CARE_NAVIGATOR_TRIGGER_SOURCE.NAV_BAR)
  const { careNavigatorModal, careNavigatorEntryPoint, openCareNavigatorModal } = useCareNavigatorModal({
    getClickEntryPoint: () =>
      triggerSourceRef.current === CARE_NAVIGATOR_TRIGGER_SOURCE.HAMBURGER
        ? COMMON_ENTRY_POINTS.HAMBURGER_MENU
        : COMMON_MIXPANEL_PAGE.GLOBAL_NAV_CARE_NAVIGATOR,
  })
  const openCareNavigatorFromHamburger = useCallback(() => {
    triggerSourceRef.current = CARE_NAVIGATOR_TRIGGER_SOURCE.HAMBURGER
    openCareNavigatorModal()
    triggerSourceRef.current = CARE_NAVIGATOR_TRIGGER_SOURCE.NAV_BAR
  }, [openCareNavigatorModal])
  const favoritedProvidersEntrypointNavProps = useFavoritedProvidersEntrypointNav()
  const { shareReferral } = useReferral()
  // Disable account badge animation after the first visit for some time.
  // The badge state should be specific to each user account, but this uses LocalStorage which is shared across users.
  // This is not ideal, but animation behavior is not critical and we don't have a way to store this per-user.
  const [animateAccountBadge, setAnimateAccountBadge] = useStorageWithExpiration(
    LOCAL_STORAGE_KEY.ANIMATE_MENU_BADGE,
    true,
    20 * 60 * 1000 /* 20 minutes */,
  )
  const mixpanelPage = useDefaultMixpanelPage()
  const isMobile = useIsMobile(Number(styles.x_tablet_min_width))
  const pressableRef = useRef<View>(null)
  const queryParams = new URLSearchParams(search)
  const showReferral = isReferralEnabled && !adultTriageDisabled

  const activeTabOverride = queryParams.get('activeTabOverride')
  const cameFromUrlRedirect = queryParams.get(FROM_URL_REDIRECT_KEY)
  const isLoggedIn = !isEmpty(user)
  const pageName = useMemo(() => convertRouteToMixpanelPage(pathname), [pathname])
  const { shouldShowFavoritedProvidersStarIcon, onFavoritedProvidersStarIconPressed } =
    favoritedProvidersEntrypointNavProps
  const isCarePage = customerName === 'care'
  const isDBT = isDBTUser(customerName)
  const { isDbtTeen, isDbtTeenInCare } = useDbtTeenCareStatus()
  const showDbtTeenEmptyState = isAuroraTheme && isDbtTeen && !isDbtTeenInCare
  const shouldRouteDbtTeenLogoToCare = getShouldRouteDbtTeenLogoToCare({
    isDbtTeenInCare,
    isLoggedIn,
    isCareManager: isLoggedIn && hasRole(user, 'ROLE_care_manager'),
    isMember: isLoggedIn && hasRole(user, 'ROLE_usr'),
  })
  const isFindEmployerPage = isCarePage && pathname === '/'
  const isRegistrationOnboardingPage = pathname.includes(PAGE_ROUTES.REGISTRATION_ONBOARDING)
  const isMFASetupPage = pathname.includes('/register/mfa')
  const isInternational = isLoggedIn ? isUserInternational : isUserAssumedInternational
  const isViewingEssentialsUngatedContent =
    window.location.pathname.startsWith(`${PAGE_ROUTES.ESSENTIALS_ARTICLES}`) ||
    (window.location.pathname.startsWith(`${PAGE_ROUTES.ESSENTIALS_EVENTS}`) && isUngatedEssentialsEventsEnabled)
  const isHomePage = ['/'].includes(pathname)
  const isLoginPage = isInLoginFlow(pathname)
  const isUnsubscribe = pathname.includes(PAGE_ROUTES.UNSUBSCRIBE)
  const shouldHeaderOverlapPageContent =
    isHomePage || beforeLaunch || isFindEmployerPage || (isAuroraTheme && isInRegisterFlow(pathname))
  const shouldShowCareNavigatorHeaderEntrypointLoggedOut = !beforeLaunch && !isCarePage
  const shouldDefaultHeaderBeOpaque = [
    PAGE_ROUTES.CARE_TEAM_PHONE_NUMBERS,
    PAGE_ROUTES.FORGOT_PASSWORD,
    PAGE_ROUTES.RESET_PASSWORD,
    PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET,
  ].includes(pathname)
  const psaBannerHasDisabledLoginAndRegistration = psaBanner?.enabled ? psaBanner.disableLoginAndRegistration : false
  const shouldHideLoginAndSignUpButtons =
    [
      PAGE_ROUTES.LOGIN,
      PAGE_ROUTES.WORKLIFE_SERVICES,
      PAGE_ROUTES.UNSUBSCRIBE,
      PAGE_ROUTES.VERIFY_ATTESTATION,
      PAGE_ROUTES.FORGOT_PASSWORD,
      PAGE_ROUTES.RESET_PASSWORD,
      PAGE_ROUTES.WORKLIFE_SERVICES_CONTACT_CARE_INTERNATIONAL,
      PAGE_ROUTES.CARE_TEAM_PHONE_NUMBERS,
      PAGE_ROUTES.REGISTRATION_ONBOARDING,
      PAGE_ROUTES.ESTABLISH_CONNECTION,
    ].includes(pathname) ||
    beforeLaunch ||
    isCarePage ||
    isViewingEssentialsUngatedContent ||
    psaBannerHasDisabledLoginAndRegistration ||
    micrositeAndElsewhereRegistrationAndLoginDisabled ||
    isInRegisterFlow(pathname)
  const shouldShowLTHeader = isLyraTherapy
  /** Logged in (and has completed registration) */
  const shouldShowLoggedInHeader = isLoggedIn && !isRegistrationOnboardingPage
  const currentPathname = activeTabOverride ?? pathname
  const pathsWithMobileHeader = [
    '/care-team-phone-numbers',
    '/secure/profile/update-location',
    '/secure/profile/update-notification-settings',
    '/secure/profile/update-personal-information',
    '/secure/profile/update-child-information',
    PAGE_ROUTES.WORKLIFE_SERVICES_CONTACT_CARE_INTERNATIONAL,
    PAGE_ROUTES.PROFILE.DEFAULT,
    PAGE_ROUTES.PROFILE.ELIGIBILITY,
    PAGE_ROUTES.PROFILE.SETTINGS,
    PAGE_ROUTES.PROFILE.SESSION_HISTORY,
    PAGE_ROUTES.PROFILE.HEALTH_PLAN_AND_PAYMENTS,
    PAGE_ROUTES.PROFILE.PRIVACY_AND_LEGAL,
  ]
  const shouldShowSearch = !isMFASetupPage && !showDbtTeenEmptyState
  const smartSearchExperimentDataTracked = useRef<null | PAGES | undefined>(null)

  useEffect(() => {
    if (pathname === '/' && !isLoggedIn) {
      resetTriage()
    }
  }, [isLoggedIn, pathname, resetTriage])

  useEffect(() => {
    if (search.includes('utm_medium=tbd')) {
      shareReferral({
        source: UTM_SOURCE.WELCOME_EMAIL,
        entryPoint: REFERRAL_ENTRY_POINTS.WELCOME_EMAIL,
      })
    }
  }, [customerName, intl.formatMessage, isMobileSized, search, setToastContent, shareReferral, trackEventWithObj])

  useEffect(() => {
    if (
      sendExperimentData &&
      isShowingAppHeader &&
      shouldShowSearch &&
      mixpanelPage.page &&
      smartSearchExperimentDataTracked.current !== mixpanelPage.page
    ) {
      sendExperimentData({
        experimentUUID: ExperimentUUIDs.SmartSearchExperimentUUID,
        joinKeys: {
          page: mixpanelPage.page,
        } as SmartSearchExperimentJoinKeys,
      })
      smartSearchExperimentDataTracked.current = mixpanelPage.page
    }
    if (!isShowingAppHeader) {
      smartSearchExperimentDataTracked.current = null
    }
  }, [isShowingAppHeader, mixpanelPage, sendExperimentData, shouldShowSearch])

  const handleLogin = () => {
    trackEventWithObj({
      event: LOGIN_EVENTS.CLICK_LOGIN,
      ...mixpanelPage,
    })
    navigate(PAGE_ROUTES.LOGIN)
  }

  const handleLogout = () => {
    setIsLogoutInProgress(true)
    trackEventWithObj({
      event: LOGIN_EVENTS.CLICK_LOG_OUT,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
    })
    mixpanel.reset()
    resetHealthPlan()
    clearWellnessCheckInFormValues()
    clearWellnessCheckInResults()
    clearWellnessGoals()
    toggleIsSessionTimerDisabled(false)
    clearPendoSession()
    updateStoredProviderResultsSurveyValues()
    localStorage.removeItem(SHARED_APP_LOGIC_LOCAL_STORAGE_KEY.TRANSFER_ACCOUNT)
    clearStorageDataOnLogout()
    broadcastSessionTermination()

    window.location.href = '/logout'
  }

  const handleProfile = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_ACCOUNT,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
      [COMMON_MIXPANEL_PROPERTIES.NOTIFICATION_BADGE_SHOWN]: shouldShowAccountBadge,
    })
    navigate(PAGE_ROUTES.PROFILE.DEFAULT, {
      state: { mixpanelEntryPoint: MIXPANEL_EVENT_ATTRIBUTES.ENTRY_POINT.NAV_BAR },
    })
  }

  const handleBenefitsClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_MY_BENEFITS,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
    })
    trackIntegratedBenefits({
      event: INTEGRATED_BENEFITS_EVENTS.VIEW_INTEGRATED_BENEFITS_PAGE,
      entryPoint: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
      action: MixpanelActions.MIXPANEL_ACTION.MENU,
    })
    navigate('/secure/index/search-care/benefits')
  }

  const handleMenuBtnClick = () => {
    setAnimateAccountBadge(false)
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_HAMBURGER_MENU,
      [COMMON_MIXPANEL_PROPERTIES.PAGE]: pageName,
      [COMMON_MIXPANEL_PROPERTIES.NOTIFICATION_BADGE_SHOWN]: shouldShowAccountBadge,
    })
  }

  const handleSignUpClick = () => {
    rerouteFromMicrosite({
      user,
      navigate,
      trackEventWithObj,
      mixpanelEvent: PATHWAYS_EVENTS.CLICK_CREATE_ACCOUNT,
      mixpanelPage: mixpanelPage?.page,
    })
  }

  const handleFaqClick = (
    e?: React.MouseEvent<HTMLButtonElement | HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    e?.preventDefault()
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_FAQS,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
    })
    if (isLoggedIn) {
      navigate('/faq')
    } else {
      navigate('/#faq')
    }
  }

  const handleReferralClick = (e?: React.MouseEvent<HTMLButtonElement>, fromLogin?: boolean) => {
    e?.preventDefault()

    shareReferral(
      fromLogin
        ? {
            source: UTM_SOURCE.LOGIN_PAGE,
            entryPoint: REFERRAL_ENTRY_POINTS.LOGIN_PAGE,
          }
        : {
            source: UTM_SOURCE.HAMBURGER_MENU,
            entryPoint: REFERRAL_ENTRY_POINTS.HAMBURGER_MENU,
          },
    )
  }

  const handleHomeClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    navigate('/secure/index/search-care')
  }

  const handleCareClick = () => {
    navigate(PAGE_ROUTES.ONE_LYRA_ASSIGNMENTS, {
      state: { [COMMON_MIXPANEL_PROPERTIES.REFERRER_PAGE]: pageName },
    })
  }

  const handleCareEmptyStateClick = () => {
    navigate(APP_ROUTE.CARE_INITIAL)
  }

  const handleSessionsClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    navigate('/secure/index/sessions')
  }

  const handleEssentialsClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    navigate(
      `/secure/index/essentials?${COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT}=${MIXPANEL_EVENT_ATTRIBUTES.ENTRY_POINT.LIBRARY}`,
      {
        state: { [COMMON_MIXPANEL_PROPERTIES.REFERRER_PAGE]: pageName },
      },
    )
  }

  const shouldShowEssentialsLT = () => !libraryDisabled && !showDbtTeenEmptyState

  const shouldShowNotInCareTabs = isLoggedIn && !isCarePage && !isLyraTherapy && !isRegistrationOnboardingPage

  const shouldShowEssentials = () => shouldShowNotInCareTabs && shouldShowEssentialsLT()

  const shouldShowHome = () => shouldShowNotInCareTabs

  const shouldShowSessions = () => shouldShowNotInCareTabs && appointmentSize > 0

  const shouldShowCare = () => shouldShowNotInCareTabs && shouldShowCareTabInitial && !isLyraSelect

  const handleLogoClick = () => {
    const generateLogoHref = () => {
      if (isLoggedIn && hasRole(user, 'ROLE_care_manager')) {
        return '/secure/care-manager/provider-matcher/'
      } else if (isLoggedIn && hasRole(user, 'ROLE_usr')) {
        return '/secure/index/'
      } else {
        return '/'
      }
    }

    trackEventWithObj({
      event: MIXPANEL_EVENTS.CLICK_HEADER_LYRA_LOGO,
      page: convertRouteToMixpanelPage(pathname, undefined, false),
    })

    const logoHref = generateLogoHref()

    if (isPathInAutoEnrollmentFlow(pathname)) {
      window.open('/', '_blank')
    } else if (isDBT && logoHref === '/') {
      //Use full page navigation so the nginx redirect (placed in nginx.conf) to the marketing page fires
      window.location.href = '/'
    } else if (shouldRouteDbtTeenLogoToCare) {
      // DBT teens in care have no Home tab - the logo routes to the Care tab instead
      handleCareClick()
    } else {
      navigate(logoHref)
    }
    if (pressableRef.current) {
      pressableRef.current.blur()
    }
  }

  const handleLanguageSelectorGetHelpPress = () => {
    trackEventWithObj({ event: MIXPANEL_EVENTS.CLICK_DONT_SEE_YOUR_LANGUAGE })
    navigate('/care-team-phone-numbers')
  }

  const handleSearchPress = () => {
    trackEventWithObj({ event: SMART_SEARCH_EVENTS.CLICK_SMART_SEARCH, ...mixpanelPage })
    navigate(PAGE_ROUTES.SMART_SEARCH)
  }

  const isNeurodiversityLandingPage = matchPaths(location.pathname, [
    '/center-of-excellence/neurodiversity',
    NEURODIVERSITY_SECURE_BASE_ROUTE,
  ])

  const isNeurodiversitySubpage =
    matchPaths(location.pathname, [
      '/secure/center-of-excellence/neurodiversity/care-options',
      '/secure/center-of-excellence/neurodiversity/contact-care-navigator',
    ]) || isNeurodiversityLandingPage

  const isHealthChallengesLoggedInLandingPage =
    isLoggedIn && matchPaths(location.pathname, ['/health-challenges', HEALTH_CHALLENGES_SECURE_BASE_ROUTE])

  const isHealthChallengesToolkitPage =
    matchPaths(location.pathname, ['/secure/health-challenges/care-options']) || isHealthChallengesLoggedInLandingPage

  const isWomensHealthLoggedInLandingPage =
    isLoggedIn && matchPaths(location.pathname, ['/womens-health', WOMENS_HEALTH_SECURE_BASE_ROUTE])

  const isWomensHealthToolkitPage =
    matchPaths(location.pathname, ['/secure/womens-health/care-options']) || isWomensHealthLoggedInLandingPage
  const handleNavigateBackFromCOEToolkit = useCallback(() => {
    switch (currentDirectPathLinkSource) {
      case DirectLinkSource.HOMEBASE_WELLNESS_TOOLKIT_CTA:
        navigate(PAGE_ROUTES.HOMEBASE)
        break
      case DirectLinkSource.TOOLKIT_DIRECTORY:
      case DirectLinkSource.CENTER_OF_EXCELLENCE_TOOLKIT:
        navigate(PAGE_ROUTES.TOOLKIT_DIRECTORY)
        break
      case DirectLinkSource.INTEGRATED_BENEFITS_NEURODIVERSITY_TOOLKIT_CTA:
      case DirectLinkSource.INTEGRATED_BENEFITS_WOMENS_HEALTH_TOOLKIT_CTA:
      case DirectLinkSource.INTEGRATED_BENEFITS_HEALTH_CHALLENGES_TOOLKIT_CTA:
        navigate(PAGE_ROUTES.SEARCH_CARE.BENEFITS)
        break
      default:
        navigate(PAGE_ROUTES.HOMEBASE)
        break
    }
    clearTriage()
    clearDirectLinkSource()
  }, [clearDirectLinkSource, clearTriage, currentDirectPathLinkSource, navigate])

  const navBarHeaderOnBack = useCallback(() => {
    // if navigated from microsite we should navigate back to homebase
    if (currentDirectPathLinkSource === DirectLinkSource.MICROSITE_FORKED_TRIAGE) {
      navigate('/')
    } else if (cameFromUrlRedirect && isNeurodiversitySubpage) {
      navigate(NEURODIVERSITY_SECURE_BASE_ROUTE, { replace: true })
    } else if (
      isNeurodiversityLandingPage ||
      isHealthChallengesLoggedInLandingPage ||
      isWomensHealthLoggedInLandingPage
    ) {
      handleNavigateBackFromCOEToolkit()
    } else {
      navigate(-1)
    }
  }, [
    cameFromUrlRedirect,
    currentDirectPathLinkSource,
    handleNavigateBackFromCOEToolkit,
    isWomensHealthLoggedInLandingPage,
    isHealthChallengesLoggedInLandingPage,
    isNeurodiversityLandingPage,
    isNeurodiversitySubpage,
    navigate,
  ])

  if (isCarePage && pathname !== '/' && !isLoggedIn) {
    return null
  }

  const isAutoEnrollmentPathwaysPage = pathname === AUTO_ENROLLMENT_PATHWAYS_PATH
  if (isAutoEnrollmentPathwaysPage) {
    return null
  }

  // The secure Care Navigator page renders the standard sub-page navigation in its own container.
  if (pathname === PAGE_ROUTES.CARE_NAVIGATOR_OPTIONS.SECURE_INDEX) {
    return null
  }

  if (videoSessionOpen) {
    return null
  }

  if (
    pathname.includes('satisfaction') ||
    pathname.includes('verify-attestation') ||
    pathname.includes(ONBOARDING_WELLNESS_GOALS_PATH) ||
    pathname.includes(PAGE_ROUTES.TOOLKIT_DIRECTORY) ||
    pathname.includes(PAGE_ROUTES.CONNECTED_SERVICE_ATTESTATION)
  ) {
    return null
  }

  // Use container headers for health plan and payment forms
  if (
    HPI_ROUTES.some(
      (hpiRoute) =>
        pathname.includes(hpiRoute) &&
        (pathname.includes(PAGE_ROUTES.PROFILE.COVERAGE_CHECK) ||
          pathname.includes(PAGE_ROUTES.HPI_COVERAGE_CHECK.EMAIL.DEFAULT)),
    )
  ) {
    return null
  }

  if (matchPaths(location.pathname, PROFILE_EDIT_PAGES) || matchPaths(location.pathname, PROFILE_CONFIRMATION_PAGES)) {
    return null
  }

  if (pathname.includes(PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT)) {
    return null
  }

  if (isMobile && isViewingEssentialsUngatedContent) {
    return null
  }

  const isDirectBookingPage = pathname.startsWith(`${APP_ROUTE.PROVIDER_PROFILE_DIRECT_BOOKING}/`)
  const isMedsOptionsExplainedPage = pathname === `/${PAGE_ROUTES.MEDS_OPTIONS_EXPLAINED.DIRECT_BOOKING}`
  const isDBTDirectBooking = isDBT && (isDirectBookingPage || isMedsOptionsExplainedPage)
  const isReschedulePage = pathname.includes(PAGE_ROUTES.SESSION_SELECTION)
  const isCoverageCheckProvider = pathname.includes('/secure/coverage-check/provider')
  const isBlueBackgroundColor = matchPaths(location.pathname, ['/secure/onboard/summary'])

  // Hide the header for when user is within assignment subpages
  if (!isMinWidthLaptop && pathname.includes('assignments/')) {
    return null
  }

  if (
    matchPaths(location.pathname, [
      '/secure/onboard/about',
      '/secure/onboard/about/:id',
      '/secure/onboard/treatment-options',
      `${APP_ROUTE.PROVIDER_PROFILE_DIRECT_BOOKING}/:id/therapy-care-options`,
      `${APP_ROUTE.PROVIDER_PROFILE_DIRECT_BOOKING}/:id/coaching-options`,
      '/secure/onboard/match-location',
      '/secure/onboard/provider/:id',
      '/secure/wellness/trauma-checkin/results',
      PAGE_ROUTES.COMING_SOON_MICROSITE,
    ])
  ) {
    // Triage, Treatment recs, and registration onboarding have custom navigation actions, so the navbar is included in the respective containers.
    return null
  } else if (
    pathname.includes('/secure/index/favorited-providers') ||
    (isDirectBookingPage && state?.isFavoritedProvidersPageRoute) ||
    pathname.includes('/secure/onboard/') ||
    pathname.includes(PAGE_ROUTES.HPI_COVERAGE_CHECK.ONBOARD.DEFAULT) ||
    pathname.includes('/secure/coverage-check/option') ||
    isCoverageCheckProvider ||
    pathname.endsWith('/secure/wellness/trauma-checkin') ||
    matchPaths(location.pathname, ['/secure/wellness/toolkit/:id'])
  ) {
    const isTraumaCheckin = pathname.endsWith('/secure/wellness/trauma-checkin')
    const isCoverageCheckOnboard = pathname.includes(PAGE_ROUTES.HPI_COVERAGE_CHECK.ONBOARD.DEFAULT)
    const isProviderResultsPage = getIsProviderResultsPage(pathname)
    const isMemberPreferencesPage = matchPaths(pathname, [
      '/secure/onboard/member-preferences',
      '/secure/onboard/member-preferences/selection',
    ])
    const isAppointmentSelectionPage = matchPaths(pathname, [`/secure/onboard/${PAGE_ROUTES.PROVIDER}/:id/schedule`])
    const navBar = (
      <OnboardingNavBar
        favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
        isStatic={
          matchPaths(pathname, [
            PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM,
            PAGE_ROUTES.CONTACT_CARE_ADVOCATE_CONFIRMATION,
          ]) && !isAuroraTheme
        }
        showBackButton={isTraumaCheckin ? false : undefined}
        shouldShowCareNavigatorHeaderEntrypoint={isTraumaCheckin ? false : undefined}
        headerBackgroundColor={
          isLyraSelect && isBlueBackgroundColor ? colors.components.s4cTriageBackgroundHeader.background : undefined
        }
      />
    )
    const isAppointmentBookingPage = pathname.endsWith('/appointment')
    if (
      isAuroraTheme &&
      (isCoverageCheckOnboard ||
        isProviderResultsPage ||
        isMemberPreferencesPage ||
        isAppointmentSelectionPage ||
        isAppointmentBookingPage ||
        isCoverageCheckProvider)
    ) {
      return <ThemeScope forceDarkMode>{navBar}</ThemeScope>
    }
    return navBar
  }

  if (
    pathname.includes(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE) ||
    pathname.includes(PAGE_ROUTES.PATHWAYS.SELF_CARE) ||
    pathname.includes(PAGE_ROUTES.FAMILY_CARE_OPTIONS) ||
    (pathname.includes(PAGE_ROUTES.SEARCH_CARE.BENEFITS) && isMobileSized) ||
    pathname === REQUIRE_ELIGIBILITY_FORM_PATH ||
    pathname === PARENT_LED_TEEN_WELCOME_PAGE_PATH ||
    isNeurodiversitySubpage ||
    isHealthChallengesToolkitPage ||
    isWomensHealthToolkitPage
  ) {
    const customHeaderProps = {
      [PAGE_ROUTES.SEARCH_CARE.BENEFITS]: {
        showBackButton: true,
        title: benefitsHeaderText,
        backgroundColor: colors.backgroundPrimary,
        shouldShowCareNavigatorHeaderEntrypoint: false,
        borderBottomColor: colors.borderDefault,
        isTransparent: false,
      },
      [REQUIRE_ELIGIBILITY_FORM_PATH]: {
        onExit: handleLogout,
        showBackButton: false,
        favoritedProvidersEntrypointNavProps: undefined,
      },
      [PARENT_LED_TEEN_WELCOME_PAGE_PATH]: {
        onExit: () => navigate(PAGE_ROUTES.SECURE_INDEX),
        showBackButton: false,
        isTransparent: false,
        favoritedProvidersEntrypointNavProps: undefined,
        borderBottomColor: 'transparent',
      },
    }

    const getCustomHeaderProps = (pathname: string): Partial<NavBarProps> => {
      if (isNeurodiversitySubpage || isHealthChallengesToolkitPage || isWomensHealthToolkitPage) {
        return {
          ...(!isNeurodiversitySubpage && {
            style: { backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundSecondary) },
          }),
          favoritedProvidersEntrypointNavProps: undefined,
          onLogoPress: () => {
            clearTriage()
            clearDirectLinkSource()
            navigate(PAGE_ROUTES.HOMEBASE)
          },
        }
      }
      return customHeaderProps[pathname] || {}
    }

    const customProps = getCustomHeaderProps(pathname)

    const navBar = (
      <NavBarHeader
        isInternational={isInternational}
        onBack={navBarHeaderOnBack}
        careNavigatorModal={careNavigatorModal}
        careNavigatorEntryPoint={careNavigatorEntryPoint}
        shouldShowCareNavigatorHeaderEntrypoint
        isTransparent
        favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
        {...customProps}
      />
    )
    return isNeurodiversitySubpage ? null : navBar
  }

  if (isMobile && pathsWithMobileHeader.includes(pathname)) {
    return <MobileHeader pathname={pathname} isClientHPI={isClientHPI} />
  }

  const headerNavMenuProps: HeaderNavMenuProps = {
    handleBenefitsPress: handleBenefitsClick,
    handleAccountPress: handleProfile,
    handleFAQsPress: handleFaqClick,
    handleReferralPress: handleReferralClick,
    handleLogOutPress: handleLogout,
    handleSessionsPress: handleSessionsClick,
    handleFavoritedProvidersStarIconPress: onFavoritedProvidersStarIconPressed,
    handleCareNavigatorPress: openCareNavigatorFromHamburger,
    shouldShowBenefits: () => !isDBTUser(customerName),
    shouldShowReferral: () => showReferral,
    currentPathname,
    trackMenuClick: handleMenuBtnClick,
    showAccountBadge: shouldShowAccountBadge,
    animateAccountBadge,
    shouldShowSessions: shouldShowLTHeader ? () => true : shouldShowSessions,
    shouldShowFavoritedProvidersStarIcon: () => !showDbtTeenEmptyState && shouldShowFavoritedProvidersStarIcon,
    shouldShowCareNavigator: () => showCareNavigatorInHamburgerMenu && shouldShowCareNavigatorHeaderEntrypoint,
  }

  const dbtShouldShowCare = isDBTDirectBooking
    ? () => appointmentSize > 0
    : isDBTTeensEnabled
      ? () => true
      : () => false

  const resolveShouldShowEssentials = () => {
    if (showDbtTeenEmptyState || isDBTDirectBooking) return false
    return shouldShowLTHeader ? shouldShowEssentialsLT() : shouldShowEssentials()
  }

  const resolveShouldShowCare = () => {
    if (showDbtTeenEmptyState) return false
    if (shouldRouteDbtTeenLogoToCare || shouldShowLTHeader) return true
    return isDBT ? dbtShouldShowCare() : shouldShowCare()
  }

  const resolveShouldShowHome = () => {
    if (showDbtTeenEmptyState) return true
    if (shouldRouteDbtTeenLogoToCare) return false
    return shouldShowLTHeader || shouldShowHome()
  }

  const mainNavItemsProps = {
    ...headerNavMenuProps,
    handleHomePress: handleHomeClick,
    handleCarePress:
      shouldRouteDbtTeenLogoToCare || shouldShowLTHeader
        ? handleCareClick
        : isDBT && isDBTTeensEnabled
          ? handleSessionsClick
          : handleCareEmptyStateClick,
    handleEssentialsPress: handleEssentialsClick,
    shouldShowEssentials: resolveShouldShowEssentials,
    shouldShowCare: resolveShouldShowCare,
    shouldShowHome: resolveShouldShowHome,
  }

  const mfaSetupPageProps = isMFASetupPage && {
    onBackPressed: () => handleLogout(),
    shouldShowBack: true,
    shouldShowNavMenu: false,
    shouldShowSearch: false,
  }

  const useGlobalHeader =
    matchPaths(pathname, GLOBAL_HEADER_ENABLED_ROUTES) && globalHeaderConfig?.web?.includes(pathname)

  const appHeader = (
    <>
      <AppHeaderVisibilityReporter setIsShowing={setIsShowingAppHeader} />
      <AppHeader
        shouldShowCareNavigatorHeaderEntrypoint={
          showDbtTeenEmptyState ? false : shouldShowCareNavigatorHeaderEntrypoint
        }
        careNavigatorModal={careNavigatorModal}
        careNavigatorEntryPoint={careNavigatorEntryPoint}
        headerNavMenuProps={headerNavMenuProps}
        mainNavItemsProps={mainNavItemsProps}
        currentPathname={currentPathname}
        onLogoPressed={handleLogoClick}
        messagesPopover={shouldShowLTHeader && <MessagesPopover shouldUseHeaderStyle />}
        showMessages={!showDbtTeenEmptyState}
        favoritedProvidersEntrypointNavProps={showDbtTeenEmptyState ? undefined : favoritedProvidersEntrypointNavProps}
        shouldShowSearch={shouldShowSearch}
        onSearchPressed={handleSearchPress}
        logoFillColor={isAuroraTheme && (isDirectBookingPage || isReschedulePage) ? colors.textInverse : logoFillColor}
        useGlobalHeader={useGlobalHeader}
        useCenteredTabSpacing={isDbtTeen}
        {...mfaSetupPageProps}
      />
    </>
  )

  const defaultHeader = (
    <AppHeader
      onLogoPressed={handleLogoClick}
      shouldShowLoginAndSignUpButtons={!shouldHideLoginAndSignUpButtons}
      onLoginPressed={handleLogin}
      onSignUpPressed={handleSignUpClick}
      shouldShowCareNavigatorHeaderEntrypoint={shouldShowCareNavigatorHeaderEntrypointLoggedOut}
      careNavigatorModal={careNavigatorModal}
      careNavigatorEntryPoint={careNavigatorEntryPoint}
      shouldShowReferral={showReferral && !isCarePage && isLoginPage}
      onReferralPressed={() => {
        handleReferralClick(undefined, true)
      }}
      isTransparentBackground={!shouldDefaultHeaderBeOpaque && isMinWidthTablet}
      shouldOverlapPageContent={shouldHeaderOverlapPageContent && isMinWidthTablet}
      shouldShowLanguageSelector={
        (isLanguageSelectorEnabled && (isHomePage || isFindEmployerPage)) ||
        beforeLaunch ||
        (isDisplayLanguageEnabled &&
          ((isLoginPage && !isCarePage) ||
            isRegistrationOnboardingPage ||
            isUnsubscribe ||
            isInRegisterFlow(pathname) ||
            isPathInNeurodiversityFlow(pathname)))
      }
      onLanguageSelectorGetHelpPress={handleLanguageSelectorGetHelpPress}
      beforeLaunch={beforeLaunch}
      logoFillColor={isAuroraTheme && isInRegisterFlow(pathname) ? colors.iconDefault : logoFillColor}
      isBlurBackground={isBlurBackground}
    />
  )

  const showAppHeader = shouldShowLoggedInHeader

  if (isAuroraTheme && (isDirectBookingPage || isReschedulePage)) {
    return showAppHeader ? (
      <ThemeScope forceDarkMode>{appHeader}</ThemeScope>
    ) : (
      <ThemeScope forceDarkMode>{defaultHeader}</ThemeScope>
    )
  }

  return showAppHeader ? appHeader : defaultHeader
}

const mapStateToProps = ($$state: $TSFixMe) => {
  const customerName = getCustomerName($$state)
  const episodes = $$state.getIn(['lyraTherapy', 'episodes'], List())
  const isLyraTherapy = !episodes.isEmpty()

  return {
    isLyraTherapy,
    appointmentSize: $$state.getIn(['appointmentDashboard', 'appointments']).size ?? 0,
    user: $$state.get('user'),
    videoSessionOpen: $$state.getIn(['lyraTherapy', 'video', 'sessionOpen']),
    worklifeEnabled: !!getCustomerWorkLifeBenefits($$state),
    hideExtraHeaderOptions: getHideExtraHeaderOptions($$state),
    libraryDisabled: getLibraryDisabled($$state),
    beforeLaunch: getBeforeLaunch($$state),
    isUserInternational: getIsUserInternational($$state),
    isClientHPI: getIsClientHPI($$state),
    customerName,
    isUserAssumedInternational: getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer($$state),
    shouldShowAccountBadge: getShouldShowAccountBadge($$state),
    currentDirectPathLinkSource: getOnboardDirectLinkSource($$state),
  }
}

const mapDispatchToProps = {
  trackEventWithObj,
  resetHealthPlan,
  resetTriage,
  setToastContent,
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
  setIsLogoutInProgress,
  clearWellnessGoals,
  clearTriage,
  clearDirectLinkSource,
}

export default connect(mapStateToProps, mapDispatchToProps)(toJS(withTrackIntegratedBenefits(Header)))
